import { useEffect, useRef, useState } from "react";
import { Eraser, Undo2 } from "lucide-react";

type Rect = { x: number; y: number; w: number; h: number };

/**
 * Lets the user black out parts of a photo (home address, birth date, etc.)
 * before upload. Only the redacted image is ever uploaded — the original never
 * leaves the device.
 */
export function RedactableImage({
  file,
  onReady,
}: {
  file: File;
  onReady: (getBlob: () => Promise<Blob | null>) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [rects, setRects] = useState<Rect[]>([]);
  const [draft, setDraft] = useState<Rect | null>(null);
  const start = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const max = 1600;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const c = canvasRef.current!;
      c.width = Math.round(img.width * scale);
      c.height = Math.round(img.height * scale);
      setRects([]);
      draw([], null);
    };
    img.src = url;
    return () => URL.revokeObjectURL(url);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  const draw = (rs: Rect[], d: Rect | null) => {
    const c = canvasRef.current;
    const img = imgRef.current;
    if (!c || !img) return;
    const ctx = c.getContext("2d")!;
    ctx.drawImage(img, 0, 0, c.width, c.height);
    ctx.fillStyle = "#000";
    [...rs, ...(d ? [d] : [])].forEach((r) => ctx.fillRect(r.x, r.y, r.w, r.h));
  };

  useEffect(() => {
    draw(rects, draft);
    onReady(
      () =>
        new Promise((res) => {
          draw(rects, null);
          canvasRef.current?.toBlob((b) => res(b), "image/jpeg", 0.9);
        }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rects, draft]);

  const pos = (e: React.PointerEvent) => {
    const c = canvasRef.current!;
    const b = c.getBoundingClientRect();
    return { x: ((e.clientX - b.left) / b.width) * c.width, y: ((e.clientY - b.top) / b.height) * c.height };
  };
  const norm = (a: { x: number; y: number }, b: { x: number; y: number }): Rect => ({
    x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y),
  });

  return (
    <div className="flex flex-col gap-2">
      <p className="font-body text-xs text-muted-foreground">
        Drag across your home address, birth date, or anything else we don't need to cover it. Keep your name, license number, license type, state and expiration date visible.
      </p>
      <canvas
        ref={canvasRef}
        className="w-full rounded-xl border border-border touch-none cursor-crosshair"
        onPointerDown={(e) => { (e.target as HTMLElement).setPointerCapture(e.pointerId); start.current = pos(e); }}
        onPointerMove={(e) => { if (start.current) setDraft(norm(start.current, pos(e))); }}
        onPointerUp={(e) => {
          if (start.current) {
            const r = norm(start.current, pos(e));
            if (r.w > 4 && r.h > 4) setRects((rs) => [...rs, r]);
          }
          start.current = null; setDraft(null);
        }}
      />
      <div className="flex gap-2">
        <button type="button" onClick={() => setRects((r) => r.slice(0, -1))} disabled={!rects.length}
          className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 font-body text-xs text-foreground disabled:opacity-40">
          <Undo2 className="h-3.5 w-3.5" /> Undo
        </button>
        <button type="button" onClick={() => setRects([])} disabled={!rects.length}
          className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 font-body text-xs text-foreground disabled:opacity-40">
          <Eraser className="h-3.5 w-3.5" /> Clear covers
        </button>
      </div>
    </div>
  );
}
